import { createContext, ReactNode, useCallback, useEffect, useReducer } from 'react';
import {
  CognitoUser,
  CognitoUserSession,
  CognitoUserAttribute,
  AuthenticationDetails,
  ISignUpResult,
} from 'amazon-cognito-identity-js';
// utils
import axios from 'src/utils/axios';
import { UserPool } from 'src/utils/auth';
// @types
import {
  AcquisitionData,
  AuthState,
  AuthUser,
  AWSCognitoContextType,
  CreateCustomerData,
  CreateMerchantData,
  ForgotPasswordResponse,
  UserType
} from '../@types/auth';
import { ActionMap } from 'src/@types/common';


const onUserConfirmedCallbacks: (() => Promise<unknown>)[] = [];

// ----------------------------------------------------------------------

const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

enum Types {
  auth = 'AUTHENTICATE',
  logout = 'LOGOUT',
}

type AwsAuthPayload = {
  [Types.auth]: {
    isAuthenticated: boolean;
    user: AuthUser | UserType | null;
  };
  [Types.logout]: undefined;
};

type AwsActions = ActionMap<AwsAuthPayload>[keyof ActionMap<AwsAuthPayload>];

const reducer = (state: AuthState, action: AwsActions) => {
  if (action.type === 'AUTHENTICATE') {
    const { isAuthenticated, user } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    };
  }
  if (action.type === 'LOGOUT') {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
    };
  }
  return state;
};

const AuthContext = createContext<AWSCognitoContextType | null>(null);

// ----------------------------------------------------------------------

type AuthProviderProps = {
  children: ReactNode;
};

function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const getUserAttributes = useCallback(
    (currentUser: CognitoUser): Promise<Record<string, any>> =>
      new Promise((resolve, reject) => {
        currentUser.getUserAttributes((err, attributes) => {
          if (err) {
            reject(err);
          } else {
            const results: Record<string, any> = {};

            attributes?.forEach((attribute) => {
              results[attribute.Name] = attribute.Value;
            });
            resolve(results);
          }
        });
      }),
    []
  );

  const getSession = useCallback(
    () =>
      new Promise((resolve, reject) => {
        const user = UserPool.getCurrentUser();
        if (user) {
          user.getSession(async (err: Error | null, session: CognitoUserSession | null) => {
            if (err) {
              reject(err);
            } else {
              const attributes = await getUserAttributes(user);
              const token = session?.getAccessToken().getJwtToken();
              // use the token or Bearer depend on the wait BE handle, by default amplify API only need to token.
              axios.defaults.headers.common.Authorization = `Bearer ${token}`;
              dispatch({
                type: Types.auth,
                payload: { isAuthenticated: true, user: attributes },
              });
              resolve({
                user,
                session,
                headers: { Authorization: `Bearer ${token}` },
              });
            }
          });
        } else {
          dispatch({
            type: Types.auth,
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      }),
    [getUserAttributes]
  );

  const initial = useCallback(async () => {
    try {
      await getSession();
    } catch {
      dispatch({
        type: Types.auth,
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    }
  }, [getSession]);

  useEffect(() => {
    initial();
  }, [initial]);

  // We make sure to handle the user update here, but return the resolve value in order for our components to be
  // able to chain additional `.then()` logic. Additionally, we `.catch` the error and "enhance it" by providing
  // a message that our React components can use.
  const login = useCallback(
    (email: string, password: string) =>
      new Promise((resolve, reject) => {
        email = email.toLowerCase();
        const user = new CognitoUser({
          Username: email,
          Pool: UserPool,
        });

        const authDetails = new AuthenticationDetails({
          Username: email,
          Password: password,
        });

        user.authenticateUser(authDetails, {
          onSuccess: (data) => {
            getSession()
                .then(() => {
                  resolve(data)
                });
          },
          onFailure: (err) => {
            if (err.code === 'UserNotConfirmedException') {
              onUserConfirmedCallbacks.push(() => login(email, password));
            }
            reject(err);
          },
          newPasswordRequired: () => {
            // Handle this on login page for update password.
            resolve({ message: 'newPasswordRequired' });
          },
        });
      }),
    [getSession]
  );

  // same thing here
  const logout = () => {
    const user = UserPool.getCurrentUser();
    if (user) {
      user.signOut();
      localStorage.clear();
      dispatch({ type: Types.logout });
    }
  };

  const getCognitoUserForUsername = (username: string) => {
    return new CognitoUser({
      Username: username.toLowerCase(),
      Pool: UserPool
    });
  }

  const forgotPasswordSubmit = (username: string, resetCode: any, newPassword: string) => {
    const cognitoUser = getCognitoUserForUsername(username);
    return new Promise((resolve, reject) => {
      cognitoUser.confirmPassword(resetCode, newPassword, {
        onSuccess: resolve,
        onFailure: reject
      })
    })
  };

  const forgotPassword = (username: string): Promise<ForgotPasswordResponse> => {
    const cognitoUser = getCognitoUserForUsername(username);
    return new Promise((resolve, reject) => {
      cognitoUser.forgotPassword({
        onSuccess: resolve,
        onFailure: reject
      })
    })
  }

  const register = (
    email: string,
    password: string,
    firstName: string,
    createCustomerData: CreateCustomerData,
    acquisitionData: AcquisitionData,
    createMerchantData?: CreateMerchantData,
  ): Promise<ISignUpResult|undefined> =>
    new Promise((resolve, reject) => {
      email = email.toLowerCase()
      const createMerchantDataString = JSON.stringify(createMerchantData)
      const createCustomerDataString = JSON.stringify(createCustomerData)
      const acquisitionDataString = JSON.stringify(acquisitionData)
      UserPool.signUp(
        email,
        password,
        [
          new CognitoUserAttribute({ Name: 'email', Value: email }),
          new CognitoUserAttribute({ Name: 'name', Value: `${firstName}` }),
          new CognitoUserAttribute({ Name: 'custom:merchant_metadata', Value: createMerchantDataString }),
          new CognitoUserAttribute({ Name: 'custom:customer_metadata', Value: createCustomerDataString }),
          new CognitoUserAttribute({ Name: 'custom:acquisition_metadata', Value: acquisitionDataString }),
        ],
        [],
        async (err, result: ISignUpResult | undefined) => {
          if (err) {
            reject(err);
            return;
          }
          resolve(result);
          if (result && result.userConfirmed) {
            login(email, password);
          } else {
            onUserConfirmedCallbacks.push(() => login(email, password));
          }
        }
      );
    });

  const confirmSignUp = (email: string, code: string) => 
    new Promise((resolve, reject) => {
      const cognitoUser = getCognitoUserForUsername(email);
      cognitoUser.confirmRegistration(code, false, (err, data) => {
        if (err) {
          reject(err); 
        } else { 
          // this approach mimics functionality of Amplify.Hub
          Promise.all(onUserConfirmedCallbacks.splice(0, onUserConfirmedCallbacks.length).map(cb => cb())).then(() => resolve(data));
        }
      });
    });


  const resendConfirmationCode = (email: string) => 
    new Promise((resolve, reject) => {
      const cognitoUser = getCognitoUserForUsername(email);
      cognitoUser.resendConfirmationCode((err, data) => { if (err) reject(err); else resolve(data) });
    });

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'cognito',
        user: state.user,
        login,
        register,
        logout,
        forgotPassword,
        forgotPasswordSubmit,
        confirmSignUp,
        resendConfirmationCode
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
